import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

// Mounts on a <form>. Child inputs trigger `submit` or `submitDebounced`,
// which call form.requestSubmit(); our submit listener intercepts the event,
// fetches the form's URL as a Turbo Stream, and applies it. The address bar
// URL is intentionally left untouched — filter state lives in the form's
// select values and in re-rendered pill/card link hrefs.
//
// We do this in JS instead of leaning on Turbo's automatic form handling
// because (a) Turbo Drive is globally disabled in this app, and (b) when the
// form lives inside a <turbo-frame>, Turbo's frame submit handler always
// returns full HTML — we want the smaller turbo-stream response.
export default class extends Controller {
  static values = { debounce: { type: Number, default: 400 } };

  connect() {
    this.timeout = null;
    this.boundSubmit = (e) => this.handleSubmit(e);
    this.element.addEventListener("submit", this.boundSubmit);
  }

  disconnect() {
    clearTimeout(this.timeout);
    this.element.removeEventListener("submit", this.boundSubmit);
  }

  submit() {
    clearTimeout(this.timeout);
    this.element.requestSubmit();
  }

  submitDebounced() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.element.requestSubmit(), this.debounceValue);
  }

  async handleSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const url = this.buildUrl();
    const response = await fetch(url, {
      headers: { Accept: "text/vnd.turbo-stream.html, text/html" },
      credentials: "same-origin",
    });
    const text = await response.text();
    Turbo.renderStreamMessage(text);
  }

  buildUrl() {
    const url = new URL(this.element.action, window.location.origin);
    if ((this.element.method || "get").toLowerCase() === "get") {
      url.search = "";
      const formData = new FormData(this.element);
      for (const [k, v] of formData.entries()) {
        if (v !== "" && k !== "utf8" && k !== "authenticity_token") {
          url.searchParams.append(k, v);
        }
      }
    }
    return url;
  }
}
